import RA from "@/components/forms/Radio.module.css";

import type { Action } from "@/components/Action.tsx";
import { Badge } from "@/components/Badge.tsx";
import { ContentPane } from "@/components/ContentPane.tsx";
import { NavigateAfterDelete } from "@/components/NavigateAfterDelete.tsx";
import { NoticeBlock } from "@/components/NoticeBlock.tsx";
import { PriceWithDiscount } from "@/components/PriceWithDiscount.tsx";
import { ResourceWithIcon } from "@/components/ResourceWithIcon.tsx";
import { SectionHeader } from "@/components/SectionHeader.tsx";
import { Separator } from "@/components/Separator.tsx";
import { Spacer } from "@/components/Spacer.tsx";
import { ViewTitle } from "@/components/Title.tsx";
import { Toolbar } from "@/components/Toolbar.tsx";
import { VatNumberText } from "@/components/VatText.tsx";
import { WTabs, type WTabItem } from "@/components/WTabs.tsx";
import { InfoTooltip, WTooltip } from "@/components/WTooltip.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WTooltipButton, type WTooltipButtonProps } from "@/components/button/WToolButton.tsx";
import { EditableButton } from "@/components/forms/EditableButton.tsx";
import { EditableText } from "@/components/forms/EditableText.tsx";
import { WSwitch } from "@/components/forms/WSwitch.tsx";
import { MaskIcon } from "@/components/icon/MaskIcon.tsx";
import { CurrencyBalance } from "@/components/l10n/Currency.tsx";
import { LongDate } from "@/components/l10n/DateFormat.tsx";
import { Percent } from "@/components/l10n/Percent.tsx";
import { ContentLoadingSuspense } from "@/components/loading/Loading.tsx";
import { DeleteModal } from "@/components/modal/DeleteModal.tsx";
import { DetailsHolder, DetailsTable } from "@/components/table/DetailsTable.tsx";
import { DetailsTableRow, type DetailsTableRowProps } from "@/components/table/DetailsTableRow.tsx";
import { WSmartTable, WTable, WTableBody, WTableLoader } from "@/components/table/WTable.tsx";
import { InvoicesTitle, TopUpInvoicesTitle, UnpaidAmountTooltip } from "@/components/themeTrans.tsx";
import { useConfig } from "@/config.ts";
import { getResourceById } from "@/modules/api/resourceTypeException.tsx";
import { getResourceLink } from "@/modules/api/resourceTypes.tsx";
import { useStandardMutation } from "@/modules/api/useStandardMutation.ts";
import { AddPaymentMethodButton, PayInvoiceButton, TopUpNowButton } from "@/modules/billing/ActionButtons.tsx";
import { AllFees } from "@/modules/billing/AllFees.tsx";
import { AutomatedTopUpModal } from "@/modules/billing/AutomatedTopUpModal.tsx";
import { accountCardMethodsAtom, getPrimaryCard, useAccountMethods } from "@/modules/billing/BoundMethod.ts";
import { LimitedBillingAccountNoticeContent } from "@/modules/billing/LimitedBillingAccountNotice.tsx";
import { LowBalanceNotificationsModal } from "@/modules/billing/LowBalanceNotificationsModal.tsx";
import { OngoingBalance } from "@/modules/billing/OngoingBalance.tsx";
import { ReportStatus } from "@/modules/billing/ReportStatus.tsx";
import { BillingAccountValidationBlock } from "@/modules/billing/account_validation/BillingAccountValidationBlock.tsx";
import { baFieldsConfigAtom } from "@/modules/billing/billingAccount/fieldsConfig.ts";
import { baLabels } from "@/modules/billing/billingAccount/fieldsInfo.tsx";
import {
    activateBillingAccountCampaignMutation,
    deleteBillingAccountMutation,
    eBillingAccountQueryAtom,
    enableDisableAutomatedTopUpMutation,
    modifyLowBalanceNoticeSettingsMutation,
    saveBillingMethodsMutation,
    setDefaultBillingAccountMutation,
} from "@/modules/billing/billingAccountQuery.ts";
import { deletePaymentCardMutation, setPaymentCardAsPrimaryMutation } from "@/modules/billing/billingCardsQuery.ts";
import { countryRegionsConfigQueryAtom } from "@/modules/billing/billingConfigQuery.ts";
import { baHistoryQueryAtom } from "@/modules/billing/billingHistoryQuery.ts";
import { baInvoiceQueryAtom } from "@/modules/billing/billingInvoicesQuery.ts";
import type { EBillingAccount } from "@/modules/billing/billingLogic.tsx";
import { baUsageQueryAtom } from "@/modules/billing/billingUsageQuery.ts";
import { InvoiceDueDate, InvoiceIssueDate, InvoicePeriod } from "@/modules/billing/invoice/InvoiceDates.tsx";
import { ExclVat, TotalExclVat } from "@/modules/billing/invoice/InvoiceView.tsx";
import {
    createCurrentUsage,
    getIsCurrentUsage,
    getPayableInvoiceId,
    invoiceName,
} from "@/modules/billing/invoice/invoiceLogic.tsx";
import { makeInvoiceLink } from "@/modules/billing/invoice/links.ts";
import {
    baLinkedResourcesAtom,
    getLinkedResourcePricing,
    type BillingLinkedResource,
    type CommitmentPricing,
    type LinkedResourcePricing,
} from "@/modules/billing/linkedResources.ts";
import { billingAccountEditLink } from "@/modules/billing/links.ts";
import { CardShortInfo, PaymentLinkIcon, PaymentMethodIcon } from "@/modules/billing/paymentMethodLogic.tsx";
import type { PaymentMethodId } from "@/modules/billing/paymentMethods.ts";
import { ReferralBox } from "@/modules/billing/referral/ReferralProgram.tsx";
import { extractCardGatewayName, getShowCardGatewayInfo } from "@/modules/billing/utils.tsx";
import type { SiteAmount } from "@/modules/currency/currencyTypes.ts";
import { ErrorRow, WErrorBoundary } from "@/modules/error/ErrorFallback.tsx";
import { CountryFlagTitle } from "@/modules/location/CountryFlag.tsx";
import { addPrices } from "@/modules/pricing/resourcePricing.ts";
import { getResourceName } from "@/modules/resource/getResourceName.ts";
import { ContactSupportButton } from "@/modules/support/ContactSupportButton.tsx";
import { useDeletableResource } from "@/utils/query/useDeletableResource.tsx";
import { useQueryResultAtom, useSuspenseQueryAtom } from "@/utils/query/useSuspenseQueryAtom.ts";
import { Trans, useLingui } from "@lingui/react/macro";
import { apiUnixToDate } from "@warrenio/api-spec/conversion";
import type { BillingAccount, BillingAccountInvoice, BillingAccountUsage } from "@warrenio/api-spec/spec.oats.gen";
import { InvoiceStatusEnum } from "@warrenio/api-spec/spec/billingEnums";
import { filterFalse } from "@warrenio/utils/collections/filterFalse";
import { getOwn } from "@warrenio/utils/collections/getOwnProperty";
import { exhaustiveSwitchCheck } from "@warrenio/utils/unreachable";
import { useAtomValue } from "jotai/react";
import { Suspense, type ReactNode } from "react";
import { Radio, RadioGroup } from "react-aria-components";

export function BillingAccountView({ baId }: { baId: number }) {
    //#region Hooks
    const accounts = useSuspenseQueryAtom(eBillingAccountQueryAtom);
    const billingAccount = useDeletableResource(() => getResourceById(accounts, baId, "billing_account"), null);

    // Progressively load invoices tab (it will appear once query completes)
    const { data } = useQueryResultAtom(baInvoiceQueryAtom(baId));
    const invoices = data ? [...data.values()] : [];

    //#endregion

    if (billingAccount === undefined) {
        return <NavigateAfterDelete />;
    }

    const reports = invoices.filter((item: BillingAccountInvoice) => item.type === "cloud_services");
    const topupInvoices = invoices.filter((item: BillingAccountInvoice) => item.type === "credit_topup");
    const topUpInvoicesTab = topupInvoices.length > 0;

    const tabs = filterFalse<WTabItem>([
        billingAccount.supportsAutomatedTopUps && {
            id: "automated",
            title: <Trans>Automated top up</Trans>,
            content: <AutomatedTopUpContent billingAccount={billingAccount} />,
        },
        billingAccount.isPrePay && {
            id: "notifications",
            title: <Trans>Low balance notifications</Trans>,
            content: <LowBalanceNotificationsContent billingAccount={billingAccount} />,
        },
        {
            id: "methods",
            title: <Trans>Payment methods</Trans>,
            content: <PaymentMethodsContent billingAccount={billingAccount} />,
        },
        billingAccount.canShareReferral && {
            id: "referral",
            title: <Trans>Referral program</Trans>,
            content: <ReferralProgramContent billingAccount={billingAccount} />,
        },
        {
            id: "reports",
            title: billingAccount.isPostPay ? <InvoicesTitle /> : <Trans>Reports</Trans>,
            content: <ReportsContent billingAccount={billingAccount} items={reports} />,
        },
        topUpInvoicesTab && {
            id: "topup",
            title: <TopUpInvoicesTitle />,
            content: <TopUpInvoicesContent billingAccount={billingAccount} items={topupInvoices} />,
        },
        {
            id: "linked",
            title: <Trans>Linked resources</Trans>,
            content: (
                // TODO: Add a nice-looking error boundary that preserves the section header (and has no "Go to dashboard")

                // Use a suspense since the resources list might take a while to load
                <WErrorBoundary tags="LinkedResourcesContent">
                    <ContentLoadingSuspense>
                        <LinkedResourcesContent billingAccount={billingAccount} />
                    </ContentLoadingSuspense>
                </WErrorBoundary>
            ),
        },
        {
            id: "balance",
            title: <Trans>Balance history</Trans>,
            content: <BalanceHistoryContent billingAccount={billingAccount} />,
        },
    ]);

    return (
        <>
            <BillingAccountToolbar billingAccount={billingAccount} />
            <BillingAccountDetails account={billingAccount} />
            <Separator />
            <WTabs autoSuspense allTab items={tabs} />
        </>
    );
}

function BillingAccountToolbar({ billingAccount }: { billingAccount: EBillingAccount }) {
    const { isDefault, isPrePay, hasVerifiedCard, id } = billingAccount;
    const setDefaultMutation = useStandardMutation(setDefaultBillingAccountMutation);

    async function setDefault() {
        await setDefaultMutation.mutateAsync(id);
    }

    return (
        <Toolbar>
            <WButton
                color="primary"
                size="bar"
                variant="ghost"
                action={async () => await setDefault()}
                isDisabled={isDefault || !hasVerifiedCard}
                icon="jp-icon-checkmark"
            >
                <Trans>Set default</Trans>
            </WButton>

            <WButton
                color="primary"
                size="bar"
                variant="ghost"
                action={billingAccountEditLink(billingAccount)}
                icon="jp-icon-edit"
            >
                <Trans>Edit</Trans>
            </WButton>

            <DeleteBillingAccountModal billingAccount={billingAccount} />

            {isPrePay && (
                <TopUpNowButton size="bar" variant="ghost" account={billingAccount}>
                    <Trans comment="Top up your billing account">Top up your account</Trans>
                </TopUpNowButton>
            )}
        </Toolbar>
    );
}

function DeleteBillingAccountModal({ billingAccount: { id, title } }: { billingAccount: EBillingAccount }) {
    const deleteMutation = useStandardMutation(deleteBillingAccountMutation);

    async function onDelete() {
        await deleteMutation.mutateAsync({ id });
    }

    return (
        <DeleteModal modalAction={onDelete}>
            <Trans>
                Deleting a billing account "{title}" will remove it permanently. Billing account can be deleted only if
                there are no unpaid resources attached to it and if it has no unspent credit.
            </Trans>
        </DeleteModal>
    );
}

interface BFieldRowProps extends DetailsTableRowProps {
    title?: ReactNode;
    children?: ReactNode;
    field: keyof typeof baLabels;
    account?: BillingAccount;
}

function BFieldRow({ title, children, field, account, ...props }: BFieldRowProps) {
    const fieldsConfig = useAtomValue(baFieldsConfigAtom);

    if (!fieldsConfig[field]?.visible) {
        return null;
    }

    let displayedValue;
    if (children === undefined && account != null) {
        const value = account[field];
        displayedValue = value ? value : "-";
    } else {
        displayedValue = children;
    }

    return (
        <DetailsTableRow title={title ?? baLabels[field]} {...props}>
            {displayedValue}
        </DetailsTableRow>
    );
}

function BillingAccountDetails({ account }: { account: EBillingAccount }) {
    const { t } = useLingui();
    const regionsConfig = useSuspenseQueryAtom(countryRegionsConfigQueryAtom);
    const { id, title, isDefault, unpaidAmount, ongoingAmount } = account;
    const rawAccount = account.account;
    const { $type, country, state_code, discount_percentage, created } = rawAccount;

    const stateNameFromList = getOwn(regionsConfig, country)?.states?.find((s) => s.code === state_code)?.name;

    const activateCampaignMutation = useStandardMutation(activateBillingAccountCampaignMutation);

    async function insertCode(campaign_code: string) {
        await activateCampaignMutation.mutateAsync({ id, campaign_code: campaign_code.trim() });
    }

    return (
        <ContentPane className="VStack gap-4">
            <AccountDetailNotices billingAccount={account} />

            <ViewTitle title={title}>
                {isDefault && (
                    // TODO: Create a `DefaultBadge` component
                    <span className="inline-flex items-center font-size-small pl-4">
                        <Badge inHeader noDot>
                            <MaskIcon className="jp-icon-checkmark size-0.75rem" />
                        </Badge>
                        <span className="pl-1">
                            <Trans>Default</Trans>
                        </span>
                    </span>
                )}
            </ViewTitle>

            <DetailsHolder>
                <DetailsTable>
                    <DetailsTableRow title={t`Account ID`}>{id}</DetailsTableRow>

                    <DetailsTableRow title={t`Account name`}>{title}</DetailsTableRow>

                    <DetailsTableRow title={t`Type`}>
                        <ResourceWithIcon type={$type} />
                    </DetailsTableRow>

                    <BFieldRow field="company_name" account={rawAccount} />
                    <BFieldRow field="company_reg_code" account={rawAccount} />
                    <BFieldRow title={<VatNumberText />} field="company_vat_number" account={rawAccount} />
                    <BFieldRow field="email" account={rawAccount} />
                    <BFieldRow field="customer_name" account={rawAccount} />
                    <BFieldRow field="customer_phone" account={rawAccount} />
                    <BFieldRow field="customer_id_number" account={rawAccount} />
                </DetailsTable>

                <Spacer />

                <DetailsTable>
                    <BFieldRow field="country">
                        <CountryFlagTitle code={country} />
                    </BFieldRow>
                    {stateNameFromList && <BFieldRow field="state_code">{stateNameFromList}</BFieldRow>}
                    <BFieldRow field="county" account={rawAccount} title={stateNameFromList ? t`County` : undefined} />
                    <BFieldRow field="city" account={rawAccount} />
                    <BFieldRow field="address_line1" account={rawAccount} />
                    <BFieldRow field="post_index" account={rawAccount} />

                    <DetailsTableRow title={t`Created`}>
                        <LongDate date={apiUnixToDate(created)} />
                    </DetailsTableRow>

                    <DetailsTableRow
                        title={t`Ongoing balance`}
                        tooltip={t`Hourly updated usage for current billing period.`}
                    >
                        <OngoingBalance value={ongoingAmount} bold />
                    </DetailsTableRow>

                    {discount_percentage > 0 && (
                        <DetailsTableRow
                            title={t`Discount`}
                            tooltip={t`A discount has been applied for all services under this billing account.`}
                        >
                            <b className="color-success">
                                <Percent value={-discount_percentage} />
                            </b>
                        </DetailsTableRow>
                    )}

                    {!!unpaidAmount && (
                        <DetailsTableRow title={t`Unpaid amount`} tooltip={<UnpaidAmountTooltip />}>
                            <b className="color-error">
                                <CurrencyBalance value={-unpaidAmount} />
                            </b>
                        </DetailsTableRow>
                    )}

                    <DetailsTableRow
                        title={t`Campaign code`}
                        tooltip={
                            account.isPrePay && !account.isOpen
                                ? t`The campaign code is activated only after a successful top up.`
                                : undefined
                        }
                    >
                        <EditableText
                            placeholder={t`Insert code`}
                            value=""
                            onChange={async (value) => await insertCode(value)}
                        />
                    </DetailsTableRow>
                </DetailsTable>
            </DetailsHolder>
        </ContentPane>
    );
}

function AutomatedTopUpContent({ billingAccount }: { billingAccount: EBillingAccount }) {
    const { t } = useLingui();
    const { minimumTopupAmount } = useConfig();
    const toggleAutomatedTopUpMutation = useStandardMutation(enableDisableAutomatedTopUpMutation);

    const { id } = billingAccount;
    const { is_recurring_payment_enabled, recurring_payment_threshold, recurring_payment_amount } =
        billingAccount.account;

    const recurringPaymentThreshold = (recurring_payment_threshold ?? minimumTopupAmount) as SiteAmount;
    const recurringPaymentAmount = (recurring_payment_amount ?? minimumTopupAmount) as SiteAmount;

    const automatedMethods = useSuspenseQueryAtom(accountCardMethodsAtom(id)).filter((m) => m.method.isAutomated);
    const primaryCardMethod = getPrimaryCard(automatedMethods);

    const needsAutomatedMethod = !automatedMethods.length;
    const canEnable = !needsAutomatedMethod && billingAccount.canTopUp;

    async function disableAutomatedTopUp() {
        await toggleAutomatedTopUpMutation.mutateAsync({ id, body: { is_recurring_payment_enabled: false } });
    }

    const switchControl = (
        <WSwitch
            isDisabled={!canEnable}
            isSelected={is_recurring_payment_enabled}
            onChange={is_recurring_payment_enabled ? disableAutomatedTopUp : /* Enabled via modal */ undefined}
        />
    );

    const content = (
        <>
            <p className="color-muted">
                <Trans>
                    We recommend automated top up as it minimizes the risk of running out of funds and thus affecting
                    your server resources.
                </Trans>
            </p>

            {is_recurring_payment_enabled && (
                <DetailsTable>
                    <DetailsTableRow thWide title={t`When balance goes below`}>
                        <AutomatedTopUpModal
                            billingAccount={billingAccount}
                            button={
                                <EditableButton action={undefined}>
                                    <span className="font-size-small">
                                        <CurrencyBalance value={recurringPaymentThreshold} />
                                    </span>
                                </EditableButton>
                            }
                        />
                    </DetailsTableRow>

                    <DetailsTableRow
                        thWide
                        title={
                            <Trans>
                                Automatically top up (min. <CurrencyBalance value={minimumTopupAmount} />)
                            </Trans>
                        }
                    >
                        <AutomatedTopUpModal
                            billingAccount={billingAccount}
                            button={
                                <EditableButton action={undefined}>
                                    <span className="font-size-small">
                                        <CurrencyBalance value={recurringPaymentAmount} />
                                    </span>
                                </EditableButton>
                            }
                        />
                    </DetailsTableRow>
                </DetailsTable>
            )}

            {is_recurring_payment_enabled && (
                <AllFees billingAccount={billingAccount} amount={recurringPaymentAmount} method={primaryCardMethod} />
            )}

            {/* TODO: Create a eg. `ButtonBar` component */}
            {needsAutomatedMethod && (
                <>
                    <p>
                        <Trans>Add a a payment method that supports automated top up.</Trans>
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <AddPaymentMethodButton onlyAutomated account={billingAccount} size="bar" variant="border" />
                    </div>
                </>
            )}

            {is_recurring_payment_enabled ? (
                switchControl
            ) : (
                <AutomatedTopUpModal billingAccount={billingAccount} button={switchControl} />
            )}
        </>
    );

    return (
        // NB: `items-start` causes the items to not stretch out horizontally
        <ContentPane className="flex flex-col items-start gap-4">
            <SectionHeader title={<Trans>Automated top up</Trans>} />
            {content}
        </ContentPane>
    );
}

function LowBalanceNotificationsContent({ billingAccount }: { billingAccount: EBillingAccount }) {
    const { t } = useLingui();
    const modifyMutation = useStandardMutation(modifyLowBalanceNoticeSettingsMutation);

    const { id } = billingAccount;
    const {
        low_balance_notice_settings: { threshold, is_enabled },
        is_recurring_payment_enabled,
    } = billingAccount.account;

    async function disableAutomatedTopUp() {
        await modifyMutation.mutateAsync({
            body: { billing_account_id: id, is_enabled: false, threshold: threshold! },
        });
    }

    const switchControl = (
        <WSwitch
            isDisabled={is_recurring_payment_enabled}
            isSelected={is_enabled}
            onChange={is_enabled ? disableAutomatedTopUp : /* Enabled via modal */ undefined}
        />
    );

    return (
        <ContentPane className="VStack items-start gap-4">
            <SectionHeader
                title={<Trans>Low balance notifications</Trans>}
                description={
                    <Trans>
                        If low balance notifications are active and the balance falls below the threshold, you will be
                        notified.
                    </Trans>
                }
            />

            {is_recurring_payment_enabled ? (
                <p>
                    <Trans>Low balance notification is not in effect because automated top up is activated.</Trans>
                </p>
            ) : is_enabled ? (
                <>
                    {threshold !== undefined && (
                        <DetailsTable className="flex items-start">
                            <DetailsTableRow thWide title={t`Notify when balance goes below`}>
                                <LowBalanceNotificationsModal
                                    billingAccount={billingAccount}
                                    button={
                                        <EditableButton action={undefined}>
                                            <span className="font-size-small">
                                                <CurrencyBalance value={threshold} />
                                            </span>
                                        </EditableButton>
                                    }
                                />
                            </DetailsTableRow>
                        </DetailsTable>
                    )}
                    {switchControl}
                </>
            ) : (
                <LowBalanceNotificationsModal billingAccount={billingAccount} button={switchControl} />
            )}
        </ContentPane>
    );
}

function PaymentMethodsContent({ billingAccount }: { billingAccount: EBillingAccount }) {
    //#region Hooks
    const { cardMethods, otherMethods } = useAccountMethods(billingAccount);
    const cards = cardMethods.map((m) => m.card);
    const primaryCard = cards.find((item) => item.is_primary);
    //#endregion

    const showCardCols = cardMethods.length > 0;
    const showCardGatewayCol = getShowCardGatewayInfo(cardMethods.map((m) => String(m.method.id)));

    const onDeleteMethodMutation = useStandardMutation(saveBillingMethodsMutation);
    const onDeleteCardMutation = useStandardMutation(deletePaymentCardMutation);
    const onSetPrimaryCardMutation = useStandardMutation(setPaymentCardAsPrimaryMutation);

    async function onDeleteMethod(methodId: PaymentMethodId) {
        const remainingMethodIds = otherMethods.filter((om) => om.id !== methodId).map((m) => m.id);
        await onDeleteMethodMutation.mutateAsync({
            billing_account_id: billingAccount.id,
            method_ids: remainingMethodIds,
        });
    }

    async function onDeleteCard(paymentObjectId: number) {
        await onDeleteCardMutation.mutateAsync({
            payment_object_id: paymentObjectId,
        });
    }

    async function onSetPrimaryCard(paymentObjectId: number) {
        await onSetPrimaryCardMutation.mutateAsync({
            payment_object_id: paymentObjectId,
        });
    }

    const tableHead = (
        <thead>
            <tr>
                <th>
                    <Trans>Method</Trans>
                </th>
                <th aria-label="Icon" />
                {showCardGatewayCol && (
                    <th>
                        <Trans comment="Payment gateway that the card is connected to">Gateway</Trans>
                    </th>
                )}
                {showCardCols && (
                    <th>
                        <Trans comment="The card that is selected for automatic charging (ie. main)">Primary</Trans>
                    </th>
                )}
                {showCardCols && (
                    <th>
                        <Trans>Status</Trans>
                    </th>
                )}
                <th aria-label="Actions" />
            </tr>
        </thead>
    );

    const cardItems = cardMethods.map((method) => {
        const { card } = method;
        const additional_data = card.parsedAdditionalData;
        const placeholder = <>**** **** **** {additional_data.last4}</>;

        return (
            <tr key={method.id}>
                <td>
                    <CardShortInfo card={card} />
                </td>
                <td>
                    <PaymentMethodIcon card={card} />
                </td>
                {showCardGatewayCol && <td>{extractCardGatewayName({ card })}</td>}
                <td>
                    {card.is_verified ? (
                        <Radio
                            className={RA.Radio}
                            value={String(card.id)}
                            isDisabled={onSetPrimaryCardMutation.isPending}
                        >
                            <Trans>Primary</Trans>
                        </Radio>
                    ) : (
                        "-"
                    )}
                </td>
                <td>
                    {card.is_verification_pending ? (
                        <Badge color="error">
                            <Trans>Unverified</Trans>
                        </Badge>
                    ) : card.is_verified ? (
                        <Badge color="success">
                            <Trans>Verified</Trans>
                        </Badge>
                    ) : (
                        <Badge color="error">
                            <Trans>Declined / Suspended</Trans>
                        </Badge>
                    )}
                </td>
                <td className="text-right">
                    <DeleteModal
                        inTable
                        isDisabled={!billingAccount.canDeletePaymentMethod}
                        modalAction={() => onDeleteCard(card.id)}
                    >
                        {additional_data.type === "paying_by_invoice" ? (
                            <Trans>
                                Deleting the invoice method will permanently remove it from the billing account.
                            </Trans>
                        ) : (
                            <Trans>
                                Deleting the credit card "{placeholder}" will permanently remove it from the billing
                                account.
                            </Trans>
                        )}
                    </DeleteModal>
                </td>
            </tr>
        );
    });

    const methodItems = otherMethods.map(({ id, method }) => {
        return (
            <tr key={id}>
                <td>{method.name}</td>
                <td>
                    <PaymentLinkIcon icon={method.id} />
                </td>
                {showCardGatewayCol && <td />}
                {showCardCols && <td>-</td>}
                {showCardCols && <td>-</td>}
                <td className="text-right">
                    <DeleteModal
                        inTable
                        isDisabled={!billingAccount.canDeletePaymentMethod}
                        modalAction={() => onDeleteMethod(method.id)}
                    >
                        <Trans>
                            Deleting a payment method "{method.name}" will remove it from the billing account.
                        </Trans>
                    </DeleteModal>
                </td>
            </tr>
        );
    });

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Payment methods</Trans>} />

            <RadioGroup
                aria-label="Primary card"
                onChange={(value) => {
                    void onSetPrimaryCard(Number(value));
                }}
                value={primaryCard?.id != null ? String(primaryCard?.id) : null}
            >
                <WTable afterTable={<AddPaymentMethodButton account={billingAccount} size="bar" variant="border" />}>
                    {tableHead}
                    <WTableBody>
                        {cardItems}
                        {methodItems}
                    </WTableBody>
                </WTable>
            </RadioGroup>
        </ContentPane>
    );
}

function ReferralProgramContent({ billingAccount }: { billingAccount: EBillingAccount }) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Referral program</Trans>} />
            <ReferralBox account={billingAccount} variant="billing_account" />
        </ContentPane>
    );
}

function mergeInvoicesWithUsage(
    invoices: BillingAccountInvoice[],
    usage: BillingAccountUsage[],
    billingAccount: BillingAccount,
): BillingAccountInvoice[] {
    const remainingInvoices = invoices.filter((inv) => inv.status !== InvoiceStatusEnum.IN_PROGRESS);
    const currentUsage = createCurrentUsage(usage, billingAccount);

    return [currentUsage, ...remainingInvoices];
}

const ViewLink = ({ invoice, action }: { invoice: BillingAccountInvoice; action: Action }) => {
    const { t } = useLingui();
    return (
        <WTooltipButton title={t`View`} color="primary" size="xs" variant="ghost" action={action}>
            {invoiceName(invoice, "short")}
        </WTooltipButton>
    );
};

const ViewButton = (props: Omit<WTooltipButtonProps, "title">) => {
    const { t } = useLingui();
    return (
        <WTooltipButton title={t`View`} icon="jp-icon-folder" color="primary" size="xs" variant="ghost" {...props} />
    );
};

function ReportsContent({
    billingAccount,
    items,
}: {
    billingAccount: EBillingAccount;
    items: BillingAccountInvoice[];
}) {
    const { id, isPostPay } = billingAccount;

    //#region Hooks
    const usage = useSuspenseQueryAtom(baUsageQueryAtom(id));
    //#endregion

    const showDueDateColumn = isPostPay;
    const withCurrentItems = mergeInvoicesWithUsage(items, usage, billingAccount.account);

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={isPostPay ? <InvoicesTitle /> : <Trans>Reports</Trans>} />

            <WSmartTable
                items={withCurrentItems}
                hasViewAllButton
                head={
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>
                                <Trans>Status</Trans>
                            </th>
                            <th>
                                <Trans>Period</Trans>
                            </th>
                            {showDueDateColumn && (
                                <th>
                                    <Trans>Due date</Trans>
                                </th>
                            )}
                            <th>
                                <InfoTooltip text={isPostPay ? <TotalExclVat /> : <ExclVat />}>
                                    <Trans>Usage</Trans>
                                </InfoTooltip>
                            </th>
                            <th />
                        </tr>
                    </thead>
                }
            >
                {(item) => {
                    const viewLink = makeInvoiceLink(item, billingAccount);
                    const payableInvoiceId = getPayableInvoiceId(item, billingAccount);
                    const { name, status, totals } = item;
                    const usage = isPostPay ? totals.total_before_tax : (totals.subtotal_resource_usage ?? 0);
                    // XXX: Why is this checking `name`?
                    const showStatus = isPostPay || !!name || getIsCurrentUsage(item);

                    return (
                        <tr key={item.id}>
                            <td>
                                <ViewLink invoice={item} action={viewLink} />
                            </td>
                            <td>{showStatus && <ReportStatus value={status} />}</td>
                            <td>{!getIsCurrentUsage(item) ? <InvoicePeriod invoice={item} /> : "-"}</td>
                            {showDueDateColumn && (
                                <td>
                                    <InvoiceDueDate invoice={item} />
                                </td>
                            )}
                            <td>{usage ? <CurrencyBalance value={usage} /> : "-"}</td>
                            <td className="text-right">
                                <div className="flex justify-end gap-1">
                                    {payableInvoiceId != null && (
                                        <PayInvoiceButton
                                            account={billingAccount}
                                            invoice_id={payableInvoiceId}
                                            size="xs"
                                            variant="ghost"
                                        />
                                    )}
                                    <ViewButton action={viewLink} />
                                </div>
                            </td>
                        </tr>
                    );
                }}
            </WSmartTable>
        </ContentPane>
    );
}

function TopUpInvoicesContent({
    billingAccount,
    items,
}: {
    billingAccount: EBillingAccount;
    items: BillingAccountInvoice[];
}) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<TopUpInvoicesTitle />} />

            <WSmartTable
                items={items}
                hasViewAllButton
                head={
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>
                                <Trans>Status</Trans>
                            </th>
                            <th>
                                <Trans comment="Date the invoice was issued">Issued</Trans>
                            </th>
                            <th>
                                <Trans comment="Total amount of the invoice">Total</Trans>
                            </th>
                            <th />
                        </tr>
                    </thead>
                }
            >
                {(item) => {
                    const viewLink = makeInvoiceLink(item, billingAccount);
                    return (
                        <tr key={item.id}>
                            <td>
                                <ViewLink invoice={item} action={viewLink} />
                            </td>
                            <td>
                                <ReportStatus value={item.status} />
                            </td>
                            <td>{item.created ? <InvoiceIssueDate invoice={item} /> : "-"}</td>
                            <td>
                                <CurrencyBalance value={item.totals.total} />
                            </td>
                            <td className="text-right">
                                <ViewButton action={viewLink} />
                            </td>
                        </tr>
                    );
                }}
            </WSmartTable>
        </ContentPane>
    );
}

interface LinkedResourceWithPricing {
    item: BillingLinkedResource;
    pricing: LinkedResourcePricing;
}

type CommitmentResource = Extract<LinkedResourceWithPricing, { pricing: CommitmentPricing }>;

function isCommitmentLinkedResource(resource: LinkedResourceWithPricing): resource is CommitmentResource {
    return resource.pricing.pricingType === "commitment";
}

function LinkedResourcesContent({ billingAccount }: { billingAccount: EBillingAccount }) {
    const { id } = billingAccount;

    const { data, error, isPending } = useAtomValue(baLinkedResourcesAtom(id));
    const resources =
        data?.map((item): LinkedResourceWithPricing => ({ item, pricing: getLinkedResourcePricing(item) })) ?? [];
    const paygResources = resources.filter(({ pricing }) => pricing.pricingType !== "commitment");
    const prepaidResources = resources.filter(isCommitmentLinkedResource);

    return (
        <>
            <PaygLinkedResourcesTable
                billingAccount={billingAccount}
                resources={paygResources}
                error={error}
                isPending={isPending}
            />

            {prepaidResources.length > 0 && <CommitmentResourcesTable resources={prepaidResources} />}
        </>
    );
}

function LinkedResourceIdentityCells({ item }: { item: BillingLinkedResource }) {
    const { t } = useLingui();

    return (
        <>
            <td>
                <WTooltip text={t`View`}>
                    <WButton color="primary" size="xs" variant="ghost" action={getResourceLink(item)}>
                        {getResourceName(item)}
                    </WButton>
                </WTooltip>
            </td>
            <td>
                <ResourceWithIcon type={item.$type} />
            </td>
        </>
    );
}

function PaygLinkedResourcesTable({
    billingAccount,
    resources,
    error,
    isPending,
}: {
    billingAccount: EBillingAccount;
    resources: LinkedResourceWithPricing[];
    error: unknown;
    isPending: boolean;
}) {
    const { id } = billingAccount;
    const prices = resources.flatMap(({ pricing }) => (pricing.pricingType === "payg" ? [pricing.price] : []));
    const totalPrice = addPrices(...prices);

    const rows = resources.map(({ item, pricing }, index) => {
        // TODO: Add a `getResourceId()` function for the `key` prop
        return (
            <tr key={index}>
                <LinkedResourceIdentityCells item={item} />
                <td>
                    {pricing.pricingType === "included" ? (
                        <span className="text-muted">
                            <Trans comment="The price has already been included in the virtual machine's price">
                                Included in VM price
                            </Trans>
                        </span>
                    ) : pricing.pricingType === "payg" ? (
                        billingAccount.account.discount_percentage ? (
                            <span className="color-success lowercase">
                                <PriceWithDiscount price={pricing.price} billingAccountId={id} allowFree />
                            </span>
                        ) : (
                            <PriceWithDiscount price={pricing.price} billingAccountId={id} allowFree />
                        )
                    ) : (
                        "-"
                    )}
                </td>
                {!!billingAccount.account.discount_percentage && (
                    <td>
                        {pricing.pricingType === "payg" ? (
                            <span className="line-through">
                                <PriceWithDiscount price={pricing.price} allowFree />
                            </span>
                        ) : (
                            "-"
                        )}
                    </td>
                )}
                <td />
            </tr>
        );
    });

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Linked resources</Trans>} />

            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                        <th>
                            <Trans>Est. monthly cost</Trans>{" "}
                            {!!billingAccount.account.discount_percentage && (
                                <span className="color-success lowercase">
                                    ({billingAccount.account.discount_percentage}% <Trans>Discount</Trans>)
                                </span>
                            )}
                        </th>
                        {!!billingAccount.account.discount_percentage && (
                            <th>
                                <Trans>Est. monthly cost</Trans> (<Trans>list price</Trans>)
                            </th>
                        )}
                        <th />
                    </tr>
                </thead>
                <WTableBody>
                    {rows}
                    {isPending && <WTableLoader />}
                    {error != null && (
                        // NB: Both the error and the loader could be shown at the same time
                        <tr>
                            <td colSpan={99}>
                                <ErrorRow error={error} tags="LinkedResourcesContent" />
                            </td>
                        </tr>
                    )}
                </WTableBody>

                {resources.length > 0 && (
                    <tfoot>
                        <tr>
                            <td />
                            <td className="text-right">
                                <Trans comment="Short of: Estimated">Est. total</Trans>:
                            </td>
                            <td>
                                {billingAccount.account.discount_percentage ? (
                                    <span className="color-success">
                                        <PriceWithDiscount price={totalPrice} billingAccountId={id} allowFree />
                                    </span>
                                ) : (
                                    <PriceWithDiscount price={totalPrice} billingAccountId={id} allowFree />
                                )}
                            </td>
                            {!!billingAccount.account.discount_percentage && (
                                <td>
                                    <span className="line-through">
                                        <PriceWithDiscount price={totalPrice} allowFree />
                                    </span>
                                </td>
                            )}
                            <td />
                        </tr>
                    </tfoot>
                )}
            </WTable>
        </ContentPane>
    );
}

function CommitmentResourcesTable({ resources }: { resources: CommitmentResource[] }) {
    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader
                title={
                    <Trans comment="Resources attached to the billing account that have already been paid for">
                        Pre-paid resources
                    </Trans>
                }
            />

            <WTable>
                <thead>
                    <tr>
                        <th>
                            <Trans>Name</Trans>
                        </th>
                        <th>
                            <Trans>Type</Trans>
                        </th>
                    </tr>
                </thead>
                <WTableBody>
                    {resources.map(({ item }, index) => (
                        // TODO: Add a `getResourceId()` function for the `key` prop
                        <tr key={index}>
                            <LinkedResourceIdentityCells item={item} />
                        </tr>
                    ))}
                </WTableBody>
            </WTable>
        </ContentPane>
    );
}

function BalanceHistoryContent({ billingAccount: { id } }: { billingAccount: EBillingAccount }) {
    const items = useSuspenseQueryAtom(baHistoryQueryAtom(id));

    return (
        <ContentPane className="VStack gap-4">
            <SectionHeader title={<Trans>Balance history</Trans>} />

            <WSmartTable
                items={items}
                hasViewAllButton
                head={
                    <thead>
                        <tr>
                            <th>
                                <Trans>Date</Trans>
                            </th>
                            <th>
                                <Trans>Amount</Trans>
                            </th>
                            <th>
                                <Trans>Description</Trans>
                            </th>
                        </tr>
                    </thead>
                }
            >
                {(item) => {
                    return (
                        <tr key={item.id}>
                            <td>{item.created ? <LongDate date={apiUnixToDate(item.created)} /> : "-"}</td>
                            <td className={item.amount > 0 ? "color-success" : "color-error"}>
                                <CurrencyBalance value={item.amount} />
                            </td>
                            <td>{item.description}</td>
                        </tr>
                    );
                }}
            </WSmartTable>
        </ContentPane>
    );
}

function AccountNotice({ billingAccount }: { billingAccount: EBillingAccount }) {
    const {
        status,
        account: { suspend_reason },
    } = billingAccount;

    switch (status) {
        case "SUSPENDED":
            return (
                <NoticeBlock color="error" icon="jp-warning-icon" button={<ContactSupportButton color="error" />}>
                    <b className="uppercase">
                        <Trans>Billing account suspended</Trans>
                    </b>
                    <br />
                    {suspend_reason}
                </NoticeBlock>
            );
        case "PENDING_VALIDATION":
            return <BillingAccountValidationBlock billing_account={billingAccount} />;
        case "ACTIVE":
            return null;
        default:
            return exhaustiveSwitchCheck(status);
    }
}

// TODO: Combine with desktop billing account notices block
export function AccountDetailNotices({ billingAccount }: { billingAccount: EBillingAccount }) {
    return (
        <>
            <AccountNotice billingAccount={billingAccount} />
            <Suspense>
                <LimitedBillingAccountNoticeContent account={billingAccount} />
            </Suspense>
        </>
    );
}
